import React, { useState, useEffect } from "react";
import { CheckCircle2, Save, Sparkles, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const AIConfigurationPage: React.FC = () => {
  const [ai, setAi] = useState({
    provider: "Anthropic Claude",
    monthly_token_quota: 5000000,
    default_model: "claude-3-5-sonnet",
    temperature: 0.7,
    auto_routing: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("ai");
      if (res.data) {
        setAi((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load AI configuration.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("ai", ai);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save AI settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading AI Copilot configuration...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">AI Copilot, LLM Model Routing & Token Quotas</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save AI Settings</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">AI Provider Engine</label>
          <select
            value={ai.provider}
            onChange={(e) => {
              setSaved(false);
              setAi({ ...ai, provider: e.target.value });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          >
            <option value="Anthropic Claude">Cloudflare Workers AI / Anthropic Claude (Claude 3.5 Sonnet)</option>
            <option value="OpenAI GPT-4">OpenAI GPT-4o Engine</option>
            <option value="Custom Azure LLM">Azure OpenAI Enterprise Service</option>
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">Monthly Token Quota</label>
          <input
            type="number"
            value={ai.monthly_token_quota}
            onChange={(e) => {
              setSaved(false);
              setAi({ ...ai, monthly_token_quota: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">Temperature (0.0 - 1.0)</label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={ai.temperature}
            onChange={(e) => {
              setSaved(false);
              setAi({ ...ai, temperature: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>

        <div className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-xl font-mono">
          <div>
            <span className="font-bold text-slate-200 font-sans block">Automatic Model Routing</span>
            <span className="text-[11px] text-slate-400 font-sans">Route queries by task complexity to balance latency & cost.</span>
          </div>
          <input
            type="checkbox"
            checked={ai.auto_routing}
            onChange={(e) => {
              setSaved(false);
              setAi({ ...ai, auto_routing: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  );
};
